import React, { useEffect, useRef, useState } from 'react';
import { FaCamera, FaImage, FaTimes } from 'react-icons/fa';
import { maintenanceExternalError } from './maintenanceExternalApi';

const maxPhotos = 3;
const maxSourceBytes = 12 * 1024 * 1024;
const targetBytes = 1.35 * 1024 * 1024;

const draftKey = issueId => `care_guardian_maintenance_draft_${issueId}`;

const canvasBlob = (canvas, quality) => new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', quality));
const fileDataUrl = file => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(new Error('Photo could not be read.'));
  reader.readAsDataURL(file);
});

const loadPhoto = async file => {
  if (typeof createImageBitmap === 'function') {
    const bitmap = await createImageBitmap(file);
    return { source: bitmap, width: bitmap.width, height: bitmap.height, close: () => bitmap.close?.() };
  }
  const url = URL.createObjectURL(file);
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve({ source: image, width: image.naturalWidth, height: image.naturalHeight, close: () => URL.revokeObjectURL(url) });
    image.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Photo could not be opened.')); };
    image.src = url;
  });
};

async function preparePhoto(file) {
  if (!/^image\/(jpeg|png)$/i.test(file.type)) throw new Error('Use a JPEG or PNG photo.');
  if (!file.size || file.size > maxSourceBytes) throw new Error('Each photo must be smaller than 12 MB.');
  const photo = await loadPhoto(file);
  const scale = Math.min(1, 1600 / Math.max(photo.width, photo.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(photo.width * scale));
  canvas.height = Math.max(1, Math.round(photo.height * scale));
  const context = canvas.getContext('2d');
  if (!context) {
    photo.close();
    throw new Error('This browser cannot prepare the photo. Try selecting a smaller JPEG image.');
  }
  context.drawImage(photo.source, 0, 0, canvas.width, canvas.height);
  photo.close();
  let quality = 0.84;
  let blob = await canvasBlob(canvas, quality);
  while (blob && blob.size > targetBytes && quality > 0.48) {
    quality -= 0.08;
    blob = await canvasBlob(canvas, quality);
  }
  if (!blob || blob.size > 4 * 1024 * 1024) throw new Error('This photo is too large to upload. Try a smaller image.');
  return { dataUrl: await fileDataUrl(blob), preview: URL.createObjectURL(blob), name: file.name || 'Photo' };
}

export default function MaintenanceUpdateComposer({ issueId, onSave }) {
  const [message, setMessage] = useState(() => {
    try { return localStorage.getItem(draftKey(issueId)) || ''; } catch { return ''; }
  });
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    try {
      if (message) localStorage.setItem(draftKey(issueId), message);
      else localStorage.removeItem(draftKey(issueId));
    } catch { /* Draft storage is optional. */ }
  }, [issueId, message]);

  useEffect(() => () => photos.forEach(photo => URL.revokeObjectURL(photo.preview)), [photos]);

  const addFiles = async fileList => {
    const available = maxPhotos - photos.length;
    const files = Array.from(fileList || []).slice(0, available);
    if (!files.length) {
      if (available <= 0) setError('You can attach up to 3 photos.');
      return;
    }
    setProcessing(true);
    setError('');
    try {
      const prepared = [];
      for (const file of files) prepared.push(await preparePhoto(file));
      setPhotos(current => [...current, ...prepared].slice(0, maxPhotos));
    } catch (reason) {
      setError(reason.message || 'A photo could not be prepared.');
    } finally {
      setProcessing(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const removePhoto = index => {
    setPhotos(current => {
      URL.revokeObjectURL(current[index]?.preview);
      return current.filter((_, photoIndex) => photoIndex !== index);
    });
  };

  const submit = async event => {
    event.preventDefault();
    if (!message.trim() && !photos.length) return;
    setBusy(true);
    setError('');
    try {
      await onSave({ message: message.trim(), images_base64: photos.map(photo => photo.dataUrl) });
      photos.forEach(photo => URL.revokeObjectURL(photo.preview));
      setMessage('');
      setPhotos([]);
      try { localStorage.removeItem(draftKey(issueId)); } catch { /* Optional. */ }
    } catch (reason) {
      setError(maintenanceExternalError(reason, 'Your comment could not be saved. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  const paste = event => {
    const files = Array.from(event.clipboardData?.items || [])
      .filter(item => item.kind === 'file' && item.type.startsWith('image/'))
      .map(item => item.getAsFile()).filter(Boolean);
    if (files.length) {
      event.preventDefault();
      addFiles(files);
    }
  };

  const drop = event => {
    event.preventDefault();
    addFiles(event.dataTransfer?.files);
  };

  return (
    <form className="maintenance-update-composer" onSubmit={submit} onDrop={drop} onDragOver={event => event.preventDefault()}>
      <div className="maintenance-update-composer__heading">
        <div><span className="maintenance-eyebrow">Comment</span><h2>Add a comment</h2></div>
        <span>{message.length}/200000</span>
      </div>
      <label htmlFor="maintenance-update-message">Comment</label>
      <textarea
        id="maintenance-update-message"
        rows="4"
        value={message}
        onChange={event => setMessage(event.target.value)}
        onPaste={paste}
        maxLength={200000}
        placeholder="Share what was checked, completed, or still needs attention."
        disabled={busy}
      />
      {photos.length > 0 && (
        <div className="maintenance-photo-previews">
          {photos.map((photo, index) => (
            <div key={`${photo.name}-${index}`}>
              <img src={photo.preview} alt={`Attachment preview ${index + 1}`} />
              <button type="button" onClick={() => removePhoto(index)} aria-label={`Remove photo ${index + 1}`}><FaTimes aria-hidden="true" /></button>
            </div>
          ))}
        </div>
      )}
      {error && <p className="maintenance-inline-error" role="alert">{error}</p>}
      <div className="maintenance-update-composer__actions">
        <input ref={inputRef} type="file" accept="image/jpeg,image/png" multiple hidden onChange={event => addFiles(event.target.files)} />
        <button type="button" className="maintenance-secondary-button" onClick={() => inputRef.current?.click()} disabled={busy || processing || photos.length >= maxPhotos}>
          <FaCamera aria-hidden="true" /> {processing ? 'Preparing...' : 'Add photos'}
        </button>
        <span><FaImage aria-hidden="true" /> {photos.length}/{maxPhotos}</span>
        <button type="submit" className="maintenance-primary-button" disabled={busy || processing || (!message.trim() && !photos.length)}>
          {busy ? 'Posting...' : 'Post comment'}
        </button>
      </div>
    </form>
  );
}
